import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigation } from '@react-navigation/core';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { z } from 'zod';
import { useForm } from '../../../../../hooks/useForm';
import { utils } from '../../../../../utils/utils';
import { STRING_CONSTANTS } from '../../../../../constants/constants';
import { AdminScreenProps } from '../../../../../typesAndInterfaces/componentProps';
import { MiniGameViewUI } from './MiniGameViewUI';
import { mbGetMediumImage } from '@mightybyte/rnw.components.image';

type Props = {
  setNumber: string;
}

export type MiniGameForm = {
  id?: string;
  title: string;
  link: string;
  imageUrl?: string;
}

const schemas = {
  id: z.string().optional(),
  title: z.string().min(1, { message: 'Title is required' }),
  link: z.string().url({ message: 'Link must be a valid URL' }),
  imageUrl: z.string().optional(),
};

const MiniGameView = ({ setNumber }: Props) => {
  const trpcUtils = trpc.useUtils();
  const navigation = useNavigation<NativeStackNavigationProp<AdminScreenProps<'MiniGames'>>>();

  const [initialIsDone, setInitialIsDone] = React.useState(setNumber === 'new');
  const [commonStartDate, setCommonStartDate] = React.useState<Date | undefined>(undefined);
  const [commonEndDate, setCommonEndDate] = React.useState<Date | undefined>(undefined);
  const [miniGames, setMiniGames] = React.useState<MiniGameForm[]>([]);
  const [currentGameIndex, setCurrentGameIndex] = React.useState<number>(0);
  const [setNumberValue, setSetNumberValue] = React.useState<number | undefined>(
    setNumber !== 'new' ? Number(setNumber) : undefined
  );

  const {
    setTitle,
    setLink,
    setImageUrl,
  } = useForm<Partial<MiniGameForm>>({ schemas }, {});

  const onBackButtonPress = () => {
    navigation.setParams({ setNumber: undefined });
  };

  // Fetch existing mini games for this set if not 'new'
  const { data: existingGames } = trpc.admin.miniGames.getGroupedBySet.useQuery(undefined, {
    enabled: setNumber !== 'new',
  });

  const { mutate: createMiniGame } = trpc.admin.miniGames.create.useMutation();
  const { mutate: updateMiniGame } = trpc.admin.miniGames.update.useMutation();
  const { mutate: updateSet } = trpc.admin.miniGames.updateSet.useMutation();
  const { mutate: deleteMiniGame } = trpc.admin.miniGames.delete.useMutation();

  // Load existing games if editing a set
  useEffect(() => {
    if (existingGames && !initialIsDone && setNumber !== 'new') {
      const setData = existingGames.find(s => s.setNumber === Number(setNumber));
      if (setData && setData.games.length > 0) {
        setInitialIsDone(true);
        setSetNumberValue(setData.setNumber);

        // Set common dates from the first game
        const firstGame = setData.games[0] as any;
        setCommonStartDate(new Date(firstGame.startDate));
        setCommonEndDate(new Date(firstGame.endDate));

        // Load all games - don't include setNumber in game objects
        const loadedGames: MiniGameForm[] = setData.games.map((game: any) => ({
          id: game.id,
          title: game.title,
          link: game.link,
          imageUrl: game.imageUrl ? mbGetMediumImage(game.imageUrl) : undefined,
        }));

        setMiniGames(loadedGames);

        // Load first game into form
        if (loadedGames[0]) {
          setTitle(loadedGames[0].title);
          setLink(loadedGames[0].link);
          setImageUrl(loadedGames[0].imageUrl);
        }
      } else {
        setInitialIsDone(true);
      }
    }
  }, [existingGames, initialIsDone, setNumber, setTitle, setLink, setImageUrl]);

  const onSaveGame = useCallback((game: MiniGameForm) => {
    // Validate the game data
    if (!game.title?.trim()) {
      utils.showPopUp('Title is required');
      return;
    }

    if (!game.link?.trim()) {
      utils.showPopUp('Link is required');
      return;
    }

    // Validate URL format
    try {
      new URL(game.link);
    } catch {
      utils.showPopUp('Link must be a valid URL');
      return;
    }

    // Validate set number from component state
    if (!setNumberValue || setNumberValue < 1) {
      utils.showPopUp('Set number is required and must be greater than 0');
      return;
    }

    if (!commonStartDate || !commonEndDate) {
      utils.showPopUp('Please set start and end dates');
      return;
    }

    const imageUrl = game.imageUrl ?? undefined;
    let imageUrlForPayload = imageUrl;
    if (imageUrl && imageUrl.includes('http')) {
      const urlParts = imageUrl.split('/');
      if (urlParts.length > 0) {
        imageUrlForPayload = urlParts[urlParts.length - 1];
      }
    }

    const payload = {
      title: game.title.trim(),
      link: game.link.trim(),
      imageUrl: imageUrlForPayload,
      setNumber: setNumberValue,
      startDate: commonStartDate.getTime(),
      endDate: commonEndDate.getTime(),
    };

    if (game.id) {
      // Update existing game
      updateMiniGame(
        { ...payload, id: game.id },
        {
          onSuccess: () => {
            trpcUtils.admin.miniGames.getGroupedBySet.invalidate();
            utils.showToast('success', 'Mini game updated successfully');

            // Update local state - keep the full URL for display
            setMiniGames(prevGames =>
              prevGames.map(g =>
                g.id === game.id
                  ? { id: g.id, title: payload.title, link: payload.link, imageUrl: game.imageUrl }
                  : g
              )
            );
          },
          onError: (error) => {
            utils.showPopUp(error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
          },
        }
      );
    } else {
      // Create new game
      createMiniGame(payload, {
        onSuccess: (createdGame: any) => {
          trpcUtils.admin.miniGames.getGroupedBySet.invalidate();
          utils.showToast('success', 'Mini game created successfully');

          // Use the imageUrl from the created game response (which has full ImageUrls object)
          const displayImageUrl = createdGame.imageUrl
            ? mbGetMediumImage(createdGame.imageUrl)
            : undefined;

          // Update local state with the created game including its ID
          setMiniGames(prevGames =>
            prevGames.map(g =>
              // Find the unsaved game by matching title and link (since it doesn't have an ID yet)
              !g.id && g.title === game.title && g.link === game.link
                ? { id: createdGame.id, title: payload.title, link: payload.link, imageUrl: displayImageUrl }
                : g
            )
          );

          // If this was a 'new' set, update the URL to reflect the set number
          if (setNumber === 'new') {
            navigation.setParams({ setNumber: setNumberValue.toString() });
          }
        },
        onError: (error) => {
          utils.showPopUp(error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
        },
      });
    }
  }, [commonStartDate, commonEndDate, setNumberValue, updateMiniGame, createMiniGame, trpcUtils, setNumber, navigation, setMiniGames]);

  const onAddNewGame = useCallback(() => {
    // Check if there's already an unsaved game
    const hasUnsavedGame = miniGames.some(game => !game.id);
    if (hasUnsavedGame) {
      utils.showPopUp('Please save the current game before adding a new one');
      return;
    }

    // Add new empty game - don't include setNumber in game object
    const newGame: MiniGameForm = {
      title: '',
      link: '',
      imageUrl: undefined,
    };

    setMiniGames([...miniGames, newGame]);

    // Switch to new game
    const newIndex = miniGames.length;
    setCurrentGameIndex(newIndex);
    setTitle('');
    setLink('');
    setImageUrl(undefined);
  }, [miniGames, setTitle, setLink, setImageUrl]);

  const onDeleteGame = useCallback((index: number) => {
    const gameToDelete = miniGames[index];

    if (gameToDelete?.id) {
      // Delete from backend
      deleteMiniGame(
        { id: gameToDelete.id },
        {
          onSuccess: () => {
            trpcUtils.admin.miniGames.getGroupedBySet.invalidate();
            utils.showToast('success', 'Mini game deleted successfully');

            // Remove from local state
            const updatedGames = miniGames.filter((_, i) => i !== index);
            setMiniGames(updatedGames);

            // Adjust current index if necessary
            if (currentGameIndex >= updatedGames.length) {
              const newIndex = Math.max(0, updatedGames.length - 1);
              setCurrentGameIndex(newIndex);
              if (updatedGames[newIndex]) {
                setTitle(updatedGames[newIndex].title);
                setLink(updatedGames[newIndex].link);
                setImageUrl(updatedGames[newIndex].imageUrl);
              }
            }
          },
          onError: (error) => utils.showToast('error', error.message ?? 'Failed to delete mini-game'),
        }
      );
    } else {
      // Just remove from local state if not saved yet
      const updatedGames = miniGames.filter((_, i) => i !== index);
      setMiniGames(updatedGames);

      if (currentGameIndex >= updatedGames.length) {
        const newIndex = Math.max(0, updatedGames.length - 1);
        setCurrentGameIndex(newIndex);
        if (updatedGames[newIndex]) {
          setTitle(updatedGames[newIndex].title);
          setLink(updatedGames[newIndex].link);
          setImageUrl(updatedGames[newIndex].imageUrl);
        }
      }
    }
  }, [miniGames, currentGameIndex, deleteMiniGame, trpcUtils, setTitle, setLink, setImageUrl]);

  // Initialize first game if starting fresh
  useEffect(() => {
    if (setNumber === 'new' && miniGames.length === 0 && initialIsDone) {
      setMiniGames([{
        title: '',
        link: '',
        imageUrl: undefined,
      }]);
    }
  }, [setNumber, miniGames.length, initialIsDone]);

  const isNewSet = useMemo(() => {
    return setNumber === 'new';
  }, [setNumber]);

  const onSaveSetInfo = useCallback(() => {
    // Validate inputs
    if (!setNumberValue || setNumberValue < 1) {
      utils.showPopUp('Set number is required and must be greater than 0');
      return;
    }

    if (!commonStartDate || !commonEndDate) {
      utils.showPopUp('Please set start and end dates');
      return;
    }

    if (commonStartDate >= commonEndDate) {
      utils.showPopUp('Start date must be before end date');
      return;
    }

    // Update all games in the set
    const currentSetNumber = Number(setNumber);

    updateSet(
      {
        currentSetNumber,
        newSetNumber: setNumberValue !== currentSetNumber ? setNumberValue : undefined,
        startDate: commonStartDate.getTime(),
        endDate: commonEndDate.getTime(),
      },
      {
        onSuccess: () => {
          trpcUtils.admin.miniGames.getGroupedBySet.invalidate();
          utils.showToast('success', 'Set info updated for all games successfully');

          // Update the URL if set number changed
          if (setNumberValue !== currentSetNumber) {
            navigation.setParams({ setNumber: setNumberValue.toString() });
          }
        },
        onError: (error) => {
          utils.showPopUp(error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
        },
      }
    );
  }, [setNumberValue, commonStartDate, commonEndDate, setNumber, updateSet, trpcUtils, navigation]);

  return (
    <MiniGameViewUI
      onBackButtonPress={onBackButtonPress}
      onSaveGame={onSaveGame}
      onAddNewGame={onAddNewGame}
      onDeleteGame={onDeleteGame}
      onSaveSetInfo={onSaveSetInfo}
      // title={title}
      setTitle={setTitle}
      // link={link}
      setLink={setLink}
      // imageUrl={imageUrl}
      setImageUrl={setImageUrl}
      setNumber={setNumberValue}
      setSetNumber={setSetNumberValue}
      commonStartDate={commonStartDate}
      setCommonStartDate={setCommonStartDate}
      commonEndDate={commonEndDate}
      setCommonEndDate={setCommonEndDate}
      isNewSet={isNewSet}
      miniGames={miniGames}
      setMiniGames={setMiniGames}
      currentGameIndex={currentGameIndex}
      setCurrentGameIndex={setCurrentGameIndex}
    />
  );
};

export { MiniGameView };

